// Who: Kirill
// When: 2026-08-20
// What: Человекочитаемое название объекта для карточек выдачи и виджета.
//
// В `property_info.name` / `room_type_info.name` лежит то, как объект назвал
// САМ ХОСТ у себя в PMS, а не то, что стоит показывать гостю: «КРД_ЦП_
// Гарантия_4», «М 692 | МАКАРОВА 5 | Шампань», «Д 207 ул. Марины Цветаевой,
// д.7», «СПБ_Кузнецовская (ДОЛГОСРОК)» — это внутренние коды: номер договора,
// метка канала, признак долгосрочной аренды. Часть хостов, наоборот, называет
// объекты нормально («Ольги Берггольц, 17», «Студия на Шаумяна»), и такие
// имена терять жалко.
//
// Поэтому не «всегда генерируем» и не «всегда показываем как есть»: имя
// пропускаем, если оно похоже на человеческое, и заменяем типом жилья, если
// похоже на служебный код. Модуль намеренно без зависимостей — его читает и
// встраиваемый виджет, у которого свой бандл.

/** «1-комнатная квартира» или «Квартира», если комнатность неизвестна. */
export const roomKindLabel = (rooms?: number | null): string => {
  if (typeof rooms !== 'number' || !Number.isFinite(rooms) || rooms < 1) {
    return 'Квартира';
  }
  return `${Math.trunc(rooms)}-комнатная квартира`;
};

/**
 * Хвостовой код в скобках: «Студия на Чайковского 56 (1)», «Апартаменты на
 * Амундсена 5 (5А)». Имя нормальное — режем только скобку.
 */
const TRAILING_CODE = /\s*\([\dA-Za-zА-Яа-яЁё]{1,4}\)\s*$/;

const SERVICE_PATTERNS: RegExp[] = [
  // Подчёркивание в названии объекта человек не пишет — это разделитель кода:
  // «КРД_ЦП_Гарантия_4», «СПБ_0_Вертикаль3», «СПБ_Кузнецовская (ДОЛГОСРОК)».
  /_/,
  // Вертикальная черта: «М 692 | МАКАРОВА 5 | Шампань».
  /\|/,
  // Кодовый префикс «буква(ы) + номер»: «Г 49, Шевелева 2», «Д 207 ул. …»,
  // «Б 317», «П 217». Букв не больше трёх, цифр не меньше двух — иначе под
  // правило попало бы нормальное «Апарт 2 на Ленина».
  /^[A-ZА-ЯЁ]{1,3}\s?\d{2,5}(?=[\s,.|\-–—]|$)/,
  // Число в скобках внутри слова: «Дом Стек(14)лянный».
  /[A-Za-zА-Яа-яЁё]\(\d+\)[A-Za-zА-Яа-яЁё]/,
];

/** Похоже ли имя на внутренний код хоста, а не на название для гостя. */
export const looksLikeServiceName = (name?: string | null): boolean => {
  const value = String(name || '').trim();
  if (!value) return true;
  return SERVICE_PATTERNS.some((pattern) => pattern.test(value));
};

export interface RoomTitleInput {
  /** Имя объекта из PMS (property_info.name или room_type_info.name). */
  name?: string | null;
  rooms?: number | null;
  /** Площадь: добавляем там, где её не показывают отдельной строкой. */
  area?: number | null;
  includeArea?: boolean;
}

/**
 * Заголовок карточки: имя хоста, если оно человеческое, иначе тип жилья.
 *
 * Адрес рядом с заголовком карточка показывает сама, так что подменённое
 * название не обезличивает объект — гость всё равно видит, где это.
 */
export const buildRoomTitle = ({
  name,
  rooms,
  area,
  includeArea = false,
}: RoomTitleInput): string => {
  const cleaned = String(name || '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(TRAILING_CODE, '')
    .trim();

  if (cleaned && !looksLikeServiceName(cleaned)) return cleaned;

  const kind = roomKindLabel(rooms);
  if (!includeArea) return kind;

  const size =
    typeof area === 'number' && Number.isFinite(area) && area > 0
      ? `, ${Math.round(area)} м²`
      : '';
  return `${kind}${size}`;
};
